import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export type UserRole = "super_admin" | "empresa_admin" | "gestor_rh" | "funcionario" | "somente_leitura";

interface AuthUser {
  user: User | null;
  session: Session | null;
  role: UserRole | null;
  empresaId: string | null;
  empresaStatus: "ativa" | "vencida" | "bloqueada" | "cancelada" | null;
  trialExpired: boolean;
  loading: boolean;
  error: Error | null;
}

interface AuthContextType extends AuthUser {
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  hasPermission: (roles: UserRole[]) => boolean;
  refreshRole: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // Unified role and company status fetching
  const { data: roleData, isLoading: roleLoading, refetch: refreshRole, error: roleError } = useQuery({
    queryKey: ["user-role", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role, empresa_id")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user role:", error);
        // Se for erro de autenticação, forçar logout
        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          await supabase.auth.signOut();
          window.location.href = '/login';
        }
        throw error;
      }

      let empresaStatus = null;
      let trialExpired = false;
      
      if (data?.empresa_id) {
        const { data: empData, error: empError } = await supabase
          .from("empresas")
          .select("status, created_at, data_vencimento, subscription_status, planos(valor)")
          .eq("id", data.empresa_id)
          .maybeSingle();

        if (empError) throw empError;
        
        empresaStatus = empData?.status || null;

        if (empData) {
          const valorPlano = (empData.planos as any)?.valor;
          const isTrial = valorPlano === 0 || !empresaStatus || empData.subscription_status === 'trial';
          const limitDate = new Date(empData.data_vencimento || new Date());
          
          if (empData.subscription_status === 'paid') {
            limitDate.setDate(limitDate.getDate() + 5);
          }
          
          if (empData.data_vencimento) {
            trialExpired = limitDate < new Date();
          }
        }
      }

      return {
        role: (data?.role as UserRole) || null,
        empresaId: data?.empresa_id || null,
        empresaStatus,
        trialExpired,
      };
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 30, // Reduzido de 5min para 30s para refletir pagamentos mais rápido
    retry: 2,
    retryDelay: 1000
  });

  useEffect(() => {
    let mounted = true;

    // Initial session check with timeout protection
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (mounted) {
          setSession(session);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth session check error:", err);
        if (mounted) setLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log("Auth state change:", event);
      
      // Se houver parâmetro de sucesso de pagamento na URL, limpar cache agressivamente
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('payment') === 'success') {
        console.log("Payment success detected in URL, clearing cache...");
        queryClient.clear();
        queryClient.invalidateQueries();
      }

      if (mounted) {
        setSession(newSession);
        
        if (event === 'SIGNED_OUT') {
          queryClient.clear();
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ["user-role"] });
          setIsRefreshing(false);
        }
        
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const hasPermission = (roles: UserRole[]) => {
    if (!roleData?.role) return false;
    return roles.includes(roleData.role);
  };

  const signIn = async (email: string, password: string) => {
    const res = await supabase.auth.signInWithPassword({ email, password });
    if (res.data.session) {
      setSession(res.data.session);
      await refreshRole();
    }
    return res;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    queryClient.clear();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const state: AuthUser = {
    user: session?.user || null,
    session,
    role: roleData?.role || null,
    empresaId: roleData?.empresaId || null,
    empresaStatus: roleData?.empresaStatus as any || null,
    trialExpired: roleData?.trialExpired || false,
    loading: loading || (!!session?.user?.id && roleLoading && !roleData) || isRefreshing,
    error: (roleError as Error) || null,
  };

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, hasPermission, refreshRole: async () => { await refreshRole(); } }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}